import * as yup from 'yup';
import type { ContestTheme, ContestThemeFont } from '../../../stores/contest';
import { fontStack, SYSTEM_FONTS } from './fonts';

export type ThemeColor = keyof ContestTheme['colors'];
export type FontPlace = keyof ContestTheme['fonts'];

/**
 * A font as edited. The list's font and the upload are both kept, so going
 * back to one after trying the other finds it as it was.
 */
export interface FontForm {
    source: ContestThemeFont['source'];
    system: { family: string; fallback: string };
    upload: { url: string | null; family: string; fallback: string };
}

/** Radii are whole pixels. */
export interface ThemeFormValues {
    colors: ContestTheme['colors'];
    fonts: Record<FontPlace, FontForm>;
    radius: { cards: number; buttons: number };
}

const DEFAULT_SYSTEM_FONT = SYSTEM_FONTS[0];
const DEFAULT_FALLBACK = fontStack(SYSTEM_FONTS[0]);

function toFontForm(font: ContestThemeFont | null): FontForm {
    const form: FontForm = {
        source: font?.source ?? 'system',
        system: {
            family: DEFAULT_SYSTEM_FONT.family,
            fallback: DEFAULT_SYSTEM_FONT.fallback,
        },
        upload: { url: null, family: '', fallback: DEFAULT_FALLBACK },
    };
    if (font?.source === 'system') {
        form.system = { family: font.family, fallback: font.fallback };
    } else if (font?.source === 'upload') {
        form.upload = {
            url: font.url,
            family: font.family,
            fallback: font.fallback,
        };
    }
    return form;
}

/**
 * The web application's own look, which a contest without a theme shows. The
 * contest takes it once its tab is saved.
 */
export const DEFAULT_THEME_VALUES: ThemeFormValues = {
    colors: {
        navBar: '#1b1b1b',
        navBarText: '#ffffff',
        pageBackground: '#ffffff',
        cardBackground: '#ffffff',
        bodyText: '#1b1b1b',
        mutedText: '#606060',
        buttonBackground: '#1b1b1b',
        buttonText: '#ffffff',
        highlightText: '#1b1b1b',
    },
    fonts: {
        titles: toFontForm(null),
        body: toFontForm(null),
    },
    radius: { cards: 16, buttons: 999 },
};

/** The pixels of a radius such as `16px`, or `fallback` for anything else. */
function pixels(radius: string, fallback: number): number {
    const match = /^(\d+)px$/.exec(radius);
    return match ? Number(match[1]) : fallback;
}

export function toThemeFormValues(theme: ContestTheme | null): ThemeFormValues {
    if (!theme) {
        return DEFAULT_THEME_VALUES;
    }
    return {
        // A copy: the store's observable cannot be posted to the preview.
        colors: { ...theme.colors },
        fonts: {
            titles: toFontForm(theme.fonts.titles),
            body: toFontForm(theme.fonts.body),
        },
        radius: {
            cards: pixels(
                theme.radius.cards,
                DEFAULT_THEME_VALUES.radius.cards
            ),
            buttons: pixels(
                theme.radius.buttons,
                DEFAULT_THEME_VALUES.radius.buttons
            ),
        },
    };
}

function toFont(form: FontForm): ContestThemeFont {
    if (form.source === 'system') {
        return { source: 'system', ...form.system };
    }
    return {
        source: 'upload',
        family: form.upload.family.trim(),
        fallback: form.upload.fallback,
        // The form schema refuses an upload without its file.
        url: form.upload.url ?? '',
    };
}

export function toTheme(values: ThemeFormValues): ContestTheme {
    return {
        colors: values.colors,
        fonts: {
            titles: toFont(values.fonts.titles),
            body: toFont(values.fonts.body),
        },
        radius: {
            cards: `${values.radius.cards}px`,
            buttons: `${values.radius.buttons}px`,
        },
    };
}

const color = yup
    .string()
    .required('Required')
    .matches(/^#[0-9a-f]{6}$/i, 'A colour written #rrggbb');

const radius = yup
    .number()
    .typeError('Whole pixels')
    .required('Required')
    .integer('Whole pixels')
    .min(0, 'At least 0')
    .max(999, 'At most 999');

function isListFontName(family: string): boolean {
    const name = family.trim().toLowerCase();
    return SYSTEM_FONTS.some((font) => font.family.toLowerCase() === name);
}

const fontSchema = yup.object({
    source: yup.string().oneOf(['system', 'upload']).required(),
    upload: yup.object().when('source', {
        is: 'upload',
        then: () =>
            yup.object({
                url: yup.string().nullable().required('Upload a woff2 file'),
                family: yup
                    .string()
                    .trim()
                    .required('Required')
                    .max(100)
                    .matches(
                        /^[\p{L}\p{N} _-]+$/u,
                        'Letters, digits, spaces, hyphens and underscores'
                    )
                    // The upload would replace that font wherever it shows.
                    .test(
                        'not-a-list-font',
                        'This name belongs to a font in the list. Use another one, such as ACT Easy Bold.',
                        (family) => !isListFontName(family ?? '')
                    ),
            }),
    }),
});

/** Two uploaded files under one name would load as one font. */
function namesTellFilesApart(fonts: Record<FontPlace, FontForm>): boolean {
    const { titles, body } = fonts;
    return (
        titles.source !== 'upload' ||
        body.source !== 'upload' ||
        titles.upload.url === body.upload.url ||
        titles.upload.family.trim().toLowerCase() !==
            body.upload.family.trim().toLowerCase()
    );
}

/** The API applies the same rules; these show them before a round trip. */
export function themeFormSchema() {
    return yup.object({
        colors: yup.object({
            navBar: color,
            navBarText: color,
            pageBackground: color,
            cardBackground: color,
            bodyText: color,
            mutedText: color,
            buttonBackground: color,
            buttonText: color,
            highlightText: color,
        }),
        fonts: yup.object({ titles: fontSchema, body: fontSchema }).test(
            'names-tell-files-apart',
            'Two different files need two names',
            (fonts, context) =>
                namesTellFilesApart(fonts as Record<FontPlace, FontForm>) ||
                context.createError({
                    path: 'fonts.body.upload.family',
                    message: 'Two different files need two names',
                })
        ),
        radius: yup.object({ cards: radius, buttons: radius }),
    });
}
